import { useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import {
  LayoutDashboard, FileText, Wrench, Building2, Briefcase, FolderKanban, Gavel,
  MessageSquare, Wallet, Bookmark, Settings, Plus, LogOut, Check, Package, ShoppingBag, Star,
  ShieldCheck, UserCircle, Clock, X, CreditCard, Send, Compass,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, ACTIVITY_LABELS } from "../lib/api";
import { RatingSummary, ReviewList, ReviewForm } from "../components/Reviews";
import { DisputeModal, RespondModal, disputeStatusChip, REASON_LABELS } from "../components/Disputes";
import { OnboardingGuide } from "../components/OnboardingGuide";

type Mine = { jobs: any[]; services: any[]; projects: any[]; people: any[]; companies: any[]; rfqs: any[] };
const peso = (n: number) => "₱" + (Number(n) || 0).toLocaleString("en-PH");
const nav = [
  ["Dashboard", LayoutDashboard], ["My Résumés", FileText], ["My Services", Wrench], ["My Company", Building2],
  ["My Jobs", Briefcase], ["My Projects", FolderKanban], ["My Bids", Gavel], ["My Applications", Send], ["Messages", MessageSquare],
  ["Wallet / Escrow", Wallet], ["Saved Items", Bookmark], ["Settings", Settings],
] as const;
const ALL_ACTIVITIES = ["find_work", "offer_service", "promote_company", "hire", "post_job", "post_project"];

export default function Dashboard() {
  const { user, loading, setUser, logout } = useAuth();
  const [, navigate] = useLocation();
  const view = new URLSearchParams(useSearch()).get("view") || "";
  const [mine, setMine] = useState<Mine | null>(null);
  const [myBids, setMyBids] = useState<any[] | null>(null);
  const [myQuotes, setMyQuotes] = useState<any[] | null>(null);
  const [myItems, setMyItems] = useState<any[] | null>(null);
  const [orders, setOrders] = useState<{ purchases: any[]; sales: any[] } | null>(null);
  const [orderBusy, setOrderBusy] = useState<string | null>(null);
  const [pendingRevs, setPendingRevs] = useState<any[]>([]);
  const [myRevs, setMyRevs] = useState<{ average: number; count: number; breakdown: Record<string, number>; received: any[]; written: any[] } | null>(null);
  const [reviewDeal, setReviewDeal] = useState<any | null>(null);
  const [verif, setVerif] = useState<{ verified: boolean; verifiedKind: string | null; request: any | null } | null>(null);
  const [verifBusy, setVerifBusy] = useState(false);
  // Mobile #9: company verification requires a completed form before submitting.
  const [coForm, setCoForm] = useState<null | { bizName: string; regType: string; regNo: string; address: string; certify: boolean }>(null);
  const [coErr, setCoErr] = useState<string | null>(null);
  const [kyc, setKyc] = useState<{ workerVerified: boolean; verification: { status: string } } | null>(null);
  const [wprofile, setWprofile] = useState<{ completeness: number; onboarded: boolean } | null>(null);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [disputeDeal, setDisputeDeal] = useState<any | null>(null);
  const [respondTo, setRespondTo] = useState<any | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [inviteBusy, setInviteBusy] = useState<string | null>(null);
  const [credit, setCredit] = useState<{ limit: number; outstanding: number; available: number } | null>(null);
  const [repayAmt, setRepayAmt] = useState("");
  const [repayBusy, setRepayBusy] = useState(false);
  const [repayErr, setRepayErr] = useState<string | null>(null);
  // v3.5.0: onboarding guide — auto-shows once right after registration, re-openable anytime.
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (!user || user.userType === "admin") return;
    try {
      if (localStorage.getItem("kph_onboarding_pending") === "1") {
        localStorage.removeItem("kph_onboarding_pending");
        localStorage.setItem("kph_onboarding_seen", "1");
        setShowGuide(true);
      }
    } catch { /* localStorage unavailable — skip auto-show */ }
  }, [user]);
  useEffect(() => { if (user) api.listMine().then((m) => m && setMine(m)); }, [user]);
  useEffect(() => { if (user) api.myBids().then((r) => setMyBids(r?.bids ?? [])).catch(() => setMyBids([])); }, [user]);
  useEffect(() => { if (user) api.myQuotes().then((r) => setMyQuotes(r?.quotes ?? [])).catch(() => setMyQuotes([])); }, [user]);
  useEffect(() => { if (user) api.myItems().then((r) => setMyItems(r ?? [])).catch(() => setMyItems([])); }, [user]);
  useEffect(() => { if (user) api.myOrders().then((r) => setOrders(r ?? { purchases: [], sales: [] })).catch(() => setOrders({ purchases: [], sales: [] })); }, [user]);
  useEffect(() => { if (user) api.pendingReviews().then((r) => setPendingRevs(r ?? [])).catch(() => setPendingRevs([])); }, [user]);
  useEffect(() => { if (user) api.myReviews().then((r) => setMyRevs(r)).catch(() => setMyRevs(null)); }, [user]);
  useEffect(() => { if (user) api.myVerification().then((v) => setVerif(v)).catch(() => setVerif(null)); }, [user]);
  useEffect(() => { if (user) api.myDisputes().then((d) => setDisputes(d ?? [])).catch(() => setDisputes([])); }, [user]);
  useEffect(() => { if (user) api.myCompanies().then((c) => setCompanies(c ?? [])).catch(() => setCompanies([])); }, [user]);
  useEffect(() => { if (user) api.myCompanyInvites().then((i) => setInvites(i ?? [])).catch(() => setInvites([])); }, [user]);
  useEffect(() => { if (user) api.getCredit().then((c) => setCredit(c)).catch(() => setCredit(null)); }, [user]);
  useEffect(() => { if (user) api.workerKyc().then((k) => setKyc(k)).catch(() => setKyc(null)); }, [user]);
  useEffect(() => { if (user && (user.capabilities || []).some((c) => c === "find_work" || c === "offer_service")) api.workerProfile().then((p) => setWprofile(p)).catch(() => setWprofile(null)); }, [user]);
  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  const caps = user.capabilities || [];
  const missing = ALL_ACTIVITIES.filter((a) => !caps.includes(a));

  const overview = [
    { n: mine?.jobs.length ?? 0, l: "Jobs Posted" },
    { n: mine?.projects.length ?? 0, l: "Projects Posted" },
    { n: (mine?.rfqs?.length ?? 0), l: "RFQs Posted" },
    { n: mine?.services.length ?? 0, l: "Services Listed" },
    { n: mine?.people.length ?? 0, l: "Résumés" },
  ];
  const listings: { kind: string; title: string; href: string }[] = mine
    ? [
        ...mine.jobs.map((x) => ({ kind: "Job", title: x.title, href: `/j/${x.id}` })),
        ...mine.projects.map((x) => ({ kind: "Project", title: x.title, href: `/project/${x.id}` })),
        ...(mine.rfqs ?? []).map((x) => ({ kind: "RFQ", title: x.title, href: `/rfq/${x.id}` })),
        ...mine.services.map((x) => ({ kind: "Service", title: x.title, href: `/s/${x.id}` })),
        ...mine.companies.map((x) => ({ kind: "Company", title: x.name, href: `/c/${x.id}` })),
        ...mine.people.map((x) => ({ kind: "Résumé", title: x.title, href: `/p/${x.id}` })),
      ]
    : [];

  async function enable(activity: string) {
    const updated = await api.addActivity(activity);
    if (updated) setUser(updated);
  }
  async function doLogout() { await logout(); navigate("/"); }

  async function refreshOrders() {
    const [o, it] = await Promise.all([api.myOrders().catch(() => null), api.myItems().catch(() => null)]);
    if (o) setOrders(o);
    if (it) setMyItems(it);
  }
  async function orderAction(id: string, action: "fulfill" | "confirm" | "cancel") {
    setOrderBusy(id + action);
    try {
      if (action === "fulfill") await api.fulfillOrder(id);
      else if (action === "confirm") await api.confirmOrder(id);
      else await api.cancelOrder(id);
      await refreshOrders();
      if (action === "confirm") api.pendingReviews().then((r) => setPendingRevs(r ?? [])).catch(() => {});
    } catch { /* surfaced via list state */ }
    finally { setOrderBusy(null); }
  }
  async function requestVerification(kind: "id" | "company", label?: string) {
    setVerifBusy(true);
    try { await api.submitVerification(kind, label); setVerif(await api.myVerification()); } catch { /* ignore */ }
    finally { setVerifBusy(false); }
  }
  async function submitCompanyVerification(e: React.FormEvent) {
    e.preventDefault();
    if (!coForm) return;
    setCoErr(null);
    if (!coForm.bizName.trim() || !coForm.regNo.trim() || !coForm.address.trim()) { setCoErr("Please complete the business name, registration number, and address."); return; }
    if (!coForm.certify) { setCoErr("Please confirm the information is accurate."); return; }
    // Only a non-sensitive summary is stored server-side (business name + registry).
    const label = `${coForm.bizName.trim()} (${coForm.regType})`.slice(0, 200);
    await requestVerification("company", label);
    setCoForm(null);
  }
  async function refreshDisputes() {
    const [d, o] = await Promise.all([api.myDisputes().catch(() => []), api.myOrders().catch(() => null)]);
    setDisputes(d ?? []);
    if (o) setOrders(o);
  }
  async function repay() {
    setRepayErr(null);
    const amt = Number(String(repayAmt).replace(/[^\d]/g, "")) || 0;
    if (amt < 1) { setRepayErr("Enter an amount."); return; }
    setRepayBusy(true);
    try { const r = await api.repayCredit(amt); setCredit({ limit: r.limit, outstanding: r.outstanding, available: r.available }); setRepayAmt(""); }
    catch (e: any) { setRepayErr(e?.message || "Could not repay."); }
    finally { setRepayBusy(false); }
  }
  async function respondInvite(memberId: string, action: "accept" | "decline") {
    setInviteBusy(memberId);
    try {
      await api.respondInvite(memberId, action);
      const [inv, cos] = await Promise.all([api.myCompanyInvites().catch(() => []), api.myCompanies().catch(() => [])]);
      setInvites(inv ?? []); setCompanies(cos ?? []);
    } catch { /* ignore */ } finally { setInviteBusy(null); }
  }
  const disputedDealIds = new Set(disputes.filter((d) => d.status === "open" || d.status === "responded").map((d) => d.dealId));
  const pendingByDeal = new Map<string, any>(pendingRevs.map((p) => [p.dealId, p]));
  async function reviewDone() {
    setReviewDeal(null);
    const [pr, mr] = await Promise.all([api.pendingReviews().catch(() => []), api.myReviews().catch(() => null)]);
    setPendingRevs(pr ?? []);
    if (mr) setMyRevs(mr);
  }
  async function archiveItem(id: string) {
    setOrderBusy(id + "archive");
    try { await api.archiveItem(id); await refreshOrders(); } catch { /* ignore */ }
    finally { setOrderBusy(null); }
  }
  const statusChip = (s: string) => {
    const map: Record<string, string> = {
      paid: "bg-navy-50 text-navy-600", fulfilled: "bg-brand-gold/15 text-[#8a5a00]",
      completed: "bg-brand-green/10 text-brand-greendark", cancelled: "bg-line text-mut",
    };
    return <span className={`chip capitalize ${map[s] || "bg-navy-50 text-navy-600"}`}>{s}</span>;
  };

  return (
    <div className="container-x py-6 grid lg:grid-cols-[240px_1fr] gap-6">
      {showGuide && <OnboardingGuide user={user} onClose={() => setShowGuide(false)} />}
      <aside className="card p-2 h-fit hidden lg:block">
        {nav.map(([label, Icon]) => {
          const LINKS: Record<string, string> = { "Messages": "/messages", "Wallet / Escrow": "/wallet", "Saved Items": "/saved", "Settings": "/settings" };
          const VIEWS: Record<string, string> = {
            "My Résumés": "people", "My Services": "services", "My Jobs": "jobs",
            "My Projects": "projects", "My Company": "companies", "My Bids": "bids",
            "My Applications": "applications",
          };
          // "My Applications" is only meaningful for workers who can find work.
          if (label === "My Applications" && !caps.includes("find_work")) return null;
          const linkTo = LINKS[label];
          const viewKey = VIEWS[label];
          const href = linkTo || (viewKey ? `/dashboard?view=${viewKey}` : "/dashboard");
          const active = label === "Dashboard" ? !view : viewKey === view && !!view;
          return (
            <Link key={label} href={href}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${active ? "bg-navy text-white" : "text-mut hover:bg-navy-50 hover:text-navy"}`}>
              <Icon className="w-4 h-4" /><span className="flex-1 text-left">{label}</span>
            </Link>
          );
        })}
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-brand-red hover:bg-brand-red/5 mt-1">
          <LogOut className="w-4 h-4" />Log out
        </button>
      </aside>

      {/* QA Tab3 #12/#16: mobile nav so Saved Items, My Applications and other sections are reachable on phones. */}
      <div className="lg:hidden flex gap-2 overflow-x-auto pb-1 -mb-2">
        {nav.map(([label, Icon]) => {
          const LINKS: Record<string, string> = { "Messages": "/messages", "Wallet / Escrow": "/wallet", "Saved Items": "/saved", "Settings": "/settings" };
          const VIEWS: Record<string, string> = { "My Résumés": "people", "My Services": "services", "My Jobs": "jobs", "My Projects": "projects", "My Company": "companies", "My Bids": "bids", "My Applications": "applications" };
          if (label === "My Applications" && !caps.includes("find_work")) return null;
          const linkTo = LINKS[label]; const viewKey = VIEWS[label];
          const href = linkTo || (viewKey ? `/dashboard?view=${viewKey}` : "/dashboard");
          const active = label === "Dashboard" ? !view : viewKey === view && !!view;
          return (
            <Link key={label} href={href} className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${active ? "bg-navy text-white border-navy" : "text-mut border-line bg-white"}`}>
              <Icon className="w-3.5 h-3.5" />{label}
            </Link>
          );
        })}
      </div>

      {view === "applications" ? (
        <MyApplications />
      ) : view ? (
        <FocusedView view={view} mine={mine} myBids={myBids} companies={companies} />
      ) : (
      <div className="min-w-0">
        {(() => {
          const ac = user.activeContext || "";
          if (!ac.startsWith("company:")) return null;
          const co = companies.find((c) => `company:${c.id}` === ac);
          return (
            <div className="card p-3 mb-4 flex items-center gap-3 border-navy-500 bg-navy-50">
              <div className="w-9 h-9 rounded-lg bg-navy text-white grid place-items-center text-xs font-bold shrink-0">{co?.initials || "Co"}</div>
              <div className="min-w-0 flex-1"><div className="text-sm font-bold text-navy truncate">Acting as {co?.name || "your company"}</div><div className="text-xs text-mut">Posts and actions default to this company context.</div></div>
              {co && (co.role === "owner" || co.role === "admin") && <Link href={`/company/${co.id}/manage`} className="btn-outline h-8 px-3 text-xs shrink-0">Manage</Link>}
            </div>
          );
        })()}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-extrabold text-navy">Welcome back, {user.firstName}</h1>
            <p className="text-mut text-sm">Here's everything happening across your account.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {user.userType !== "admin" && (
              <button onClick={() => setShowGuide(true)} className="btn-outline"><Compass className="w-4 h-4" />Getting started</button>
            )}
            <Link href={`/u/${user.id}`} className="btn-outline"><UserCircle className="w-4 h-4" />My Profile</Link>
            {user.userType === "admin" && <Link href="/admin" className="btn-outline"><ShieldCheck className="w-4 h-4" />Admin</Link>}
            <Link href="/post/job" className="btn-navy"><Plus className="w-4 h-4" />Post</Link>
          </div>
        </div>

        {/* Worker profile completeness — Increment 27 */}
        {caps.some((c) => c === "find_work" || c === "offer_service") && wprofile && wprofile.completeness < 100 && (
          <div className="card p-5 mb-5">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="font-bold text-navy flex items-center gap-2"><UserCircle className="w-4 h-4" />{wprofile.onboarded ? "Complete your worker profile" : "Set up your worker profile"}</div>
              <Link href="/worker-profile" className="btn-outline h-8 px-3 text-xs shrink-0">{wprofile.onboarded ? "Edit profile" : "Start onboarding"}</Link>
            </div>
            <div className="h-2 rounded-full bg-navy-50 overflow-hidden"><div className="h-full bg-brand-green transition-all" style={{ width: `${wprofile.completeness}%` }} /></div>
            <p className="text-xs text-mut mt-2">{wprofile.completeness}% complete — a fuller profile ranks higher and wins more work.</p>
          </div>
        )}

        {/* Worker KYC status — Increment 25 */}
        {caps.some((c) => c === "find_work" || c === "offer_service") && kyc && !kyc.workerVerified && kyc.verification.status !== "approved" && (
          <div className="card p-5 mb-5 border-l-4 border-brand-red">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-red/10 grid place-items-center shrink-0"><ShieldCheck className="w-5 h-5 text-brand-red" /></div>
              <div className="flex-1 min-w-0">
                {kyc.verification.status === "pending" ? (
                  <>
                    <div className="font-bold text-navy">Worker verification under review</div>
                    <div className="text-sm text-mut">We're reviewing your documents. Your profile goes live once approved.</div>
                  </>
                ) : kyc.verification.status === "rejected" ? (
                  <>
                    <div className="font-bold text-navy">Verification needs changes</div>
                    <div className="text-sm text-mut">Update your documents and resubmit to become hireable.</div>
                  </>
                ) : (
                  <>
                    <div className="font-bold text-navy">Get verified to be hireable</div>
                    <div className="text-sm text-mut">Clients can only find and hire verified workers. Verify your identity to go live.</div>
                  </>
                )}
              </div>
              <Link href="/get-verified" className="btn-red h-9 shrink-0">
                {kyc.verification.status === "pending" ? "View status" : kyc.verification.status === "rejected" ? "Resubmit" : "Get verified"}
              </Link>
            </div>
          </div>
        )}
        {caps.some((c) => c === "find_work" || c === "offer_service") && (kyc?.workerVerified || kyc?.verification?.status === "approved") && (
          <div className="card p-4 mb-5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 grid place-items-center"><ShieldCheck className="w-5 h-5 text-emerald-700" /></div>
            <div className="text-sm"><span className="font-bold text-navy">Verified worker</span> <span className="text-mut">— your profile is live and hireable.</span></div>
          </div>
        )}

        {/* Verification status — Increment 14 */}
        <div className="card p-5 mb-5">
          {!verif ? (
            <div className="text-sm text-mut">Loading verification status…</div>
          ) : verif.verified ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-green/10 grid place-items-center"><ShieldCheck className="w-5 h-5 text-brand-green" /></div>
              <div>
                <div className="font-bold text-navy flex items-center gap-2">You're verified <span className="chip bg-brand-green/10 text-brand-greendark capitalize">{verif.verifiedKind === "company" ? "Company" : "Verified ID"}</span></div>
                <p className="text-xs text-mut">The Verified badge shows on your profile and listings.</p>
              </div>
            </div>
          ) : verif.request?.status === "pending" ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-gold/10 grid place-items-center"><Clock className="w-5 h-5 text-brand-gold" /></div>
              <div>
                <div className="font-bold text-navy">Verification under review</div>
                <p className="text-xs text-mut">Your {verif.request.kind === "company" ? "company" : "identity"} verification is pending admin approval.</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-navy-50 grid place-items-center shrink-0"><ShieldCheck className="w-5 h-5 text-navy" /></div>
                <div className="min-w-0">
                  <div className="font-bold text-navy">Get verified</div>
                  <p className="text-xs text-mut">Verified accounts earn a trust badge across the marketplace.{verif.request?.status === "rejected" && verif.request?.decisionNote ? ` Previous attempt: ${verif.request.decisionNote}` : ""}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => requestVerification("id")} disabled={verifBusy} className="btn-outline h-9 text-xs disabled:opacity-50">Verify my identity</button>
                <button onClick={() => { setCoErr(null); setCoForm({ bizName: "", regType: "DTI", regNo: "", address: "", certify: false }); }} disabled={verifBusy} className="btn-navy h-9 text-xs disabled:opacity-50">Verify my company</button>
              </div>
            </div>
          )}
        </div>

        {/* Your activities — the multi-role model, live */}
        <div className="card p-5 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-navy">Your Activities</h3>
            <span className="text-xs text-mut">One account, multiple activities — add more anytime</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {caps.length === 0 && <span className="text-sm text-mut">No activities yet.</span>}
            {caps.map((c) => (
              <span key={c} className="chip bg-brand-green/10 text-brand-greendark"><Check className="w-3.5 h-3.5" />{ACTIVITY_LABELS[c] || c}</span>
            ))}
          </div>
          {missing.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center pt-3 border-t border-line">
              <span className="text-xs font-semibold text-mut">Enable:</span>
              {missing.map((a) => (
                <button key={a} onClick={() => enable(a)} className="chip bg-navy-50 text-navy-600 hover:bg-navy hover:text-white transition-colors">
                  <Plus className="w-3.5 h-3.5" />{ACTIVITY_LABELS[a] || a}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Vendor credit — Increment 22 */}
        {credit && credit.limit > 0 && (
          <div className="card p-5 mb-5">
            <div className="flex items-center gap-2 mb-3"><CreditCard className="w-4 h-4 text-navy" /><h3 className="font-bold text-navy">Credit Line</h3></div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center"><div className="text-xl font-extrabold text-navy">{peso(credit.limit)}</div><div className="text-[11px] text-mut">Limit</div></div>
              <div className="text-center"><div className="text-xl font-extrabold text-brand-red">{peso(credit.outstanding)}</div><div className="text-[11px] text-mut">Outstanding</div></div>
              <div className="text-center"><div className="text-xl font-extrabold text-brand-green">{peso(credit.available)}</div><div className="text-[11px] text-mut">Available</div></div>
            </div>
            {credit.outstanding > 0 ? (
              <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-line">
                <input className="field flex-1" inputMode="numeric" value={repayAmt} onChange={(e) => setRepayAmt(e.target.value)} placeholder={`Repay from wallet (up to ${peso(credit.outstanding)})`} />
                <button onClick={repay} disabled={repayBusy} className="btn-navy disabled:opacity-60">{repayBusy ? "…" : "Repay"}</button>
              </div>
            ) : <p className="text-xs text-mut pt-2 border-t border-line">You have no outstanding balance. Choose "Credit" at checkout in the Materials store to buy now and pay later.</p>}
            {repayErr && <p className="text-xs text-brand-red mt-2">{repayErr}</p>}
          </div>
        )}

        {/* Company invites — Increment 17 */}
        {invites.length > 0 && (
          <div className="card p-5 mb-5 border-brand-red/30">
            <div className="flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-brand-red" /><h3 className="font-bold text-navy">Company invitations</h3></div>
            <div className="space-y-2">
              {invites.map((iv) => (
                <div key={iv.id} className="flex items-center justify-between border border-line rounded-lg p-3 gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-navy text-white grid place-items-center text-xs font-bold shrink-0">{iv.initials}</div>
                    <div className="min-w-0"><div className="text-sm font-semibold text-navy truncate">{iv.companyName}</div><div className="text-xs text-mut">Invited as {iv.role}</div></div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => respondInvite(iv.id, "accept")} disabled={inviteBusy === iv.id} className="btn-navy h-8 px-3 text-xs disabled:opacity-50">Accept</button>
                    <button onClick={() => respondInvite(iv.id, "decline")} disabled={inviteBusy === iv.id} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">Decline</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Companies — Increment 17 */}
        {companies.length > 0 && (
          <div id="sec-companies" className="card p-5 mb-5 scroll-mt-20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-navy flex items-center gap-2"><Building2 className="w-4 h-4" />My Companies</h3>
              <Link href="/post/company" className="text-xs text-brand-red font-semibold">+ New company</Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {companies.map((c) => (
                <div key={c.id} className="flex items-center justify-between border border-line rounded-lg p-3 gap-2 min-w-0">
                  <Link href={`/c/${c.id}`} className="flex items-center gap-2 min-w-0 hover:text-brand-red">
                    <div className="w-9 h-9 rounded-lg bg-navy text-white grid place-items-center text-xs font-bold shrink-0">{c.initials}</div>
                    <div className="min-w-0"><div className="text-sm font-semibold text-navy truncate">{c.name}</div><div className="text-xs text-mut capitalize">{c.role}</div></div>
                  </Link>
                  {(c.role === "owner" || c.role === "admin") && <Link href={`/company/${c.id}/manage`} className="btn-outline h-8 px-3 text-xs shrink-0">Manage</Link>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {overview.map((o) => (
            <div key={o.l} className="card p-4 text-center"><div className="text-2xl font-extrabold text-navy">{o.n}</div><div className="text-xs text-mut mt-1">{o.l}</div></div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div id="sec-listings" className="card p-5 lg:col-span-2 scroll-mt-20">
            <h3 className="font-bold text-navy mb-3">My Listings</h3>
            <div className="space-y-2">
              {listings.length === 0 && (
                <div className="text-sm text-mut border border-dashed border-line rounded-lg p-6 text-center">
                  Nothing posted yet. <Link href="/post/job" className="text-brand-red font-semibold">Post a job</Link> or <Link href="/post/service" className="text-brand-red font-semibold">offer a service</Link> to get started.
                </div>
              )}
              {listings.map((l, i) => (
                <Link key={i} href={l.href} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                  <span className="text-sm text-navy"><span className="chip bg-navy-50 text-navy-600 mr-2">{l.kind}</span>{l.title}</span>
                  <span className="text-xs text-brand-green whitespace-nowrap ml-3">● Live</span>
                </Link>
              ))}
            </div>
          </div>
          <div className="card p-5">
            <h3 className="font-bold text-navy mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <Link href="/post/job" className="btn-outline w-full justify-start">Post a Job</Link>
              <Link href="/post/project" className="btn-outline w-full justify-start">Post a Project</Link>
              <Link href="/post/service" className="btn-outline w-full justify-start">Offer a Service</Link>
              <Link href="/post/item" className="btn-outline w-full justify-start">Sell an Item / Equipment</Link>
            </div>
          </div>
        </div>

        {/* My Bids — Increment 3 */}
        {myBids && myBids.length > 0 && (
          <div id="sec-bids" className="card p-5 mt-4 scroll-mt-20">
            <div className="flex items-center gap-2 mb-3">
              <Gavel className="w-4 h-4 text-navy" />
              <h3 className="font-bold text-navy">My Bids</h3>
              <span className="text-xs text-mut">Your sealed prices are visible only to you and the project owner.</span>
            </div>
            <div className="space-y-2">
              {myBids.map((b) => (
                <Link key={b.id} href={`/project/${b.projectId}`} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                  <span className="text-sm text-navy min-w-0">
                    <span className="font-medium">{b.projectTitle || b.projectId}</span>
                    <span className="text-mut"> · {peso(b.status === "awarded" && b.finalPrice != null ? b.finalPrice : b.price)}{b.durationText ? ` · ${b.durationText}` : ""}</span>
                  </span>
                  <span className="text-xs whitespace-nowrap ml-3">
                    {b.status === "awarded" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">Awarded</span>}
                    {b.status === "in_negotiation" && <span className="chip bg-brand-gold/10 text-[#8a5a00]">Negotiating</span>}
                    {b.status === "shortlisted" && <span className="chip bg-brand-green/10 text-brand-greendark">Shortlisted</span>}
                    {b.status === "submitted" && <span className="chip bg-navy-50 text-navy-600">Submitted</span>}
                    {b.status === "rejected" && <span className="chip bg-line text-mut">Not selected</span>}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* My Quotes — Increment 7 */}
        {myQuotes && myQuotes.length > 0 && (
          <div className="card p-5 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-navy" />
              <h3 className="font-bold text-navy">My Quotes</h3>
              <span className="text-xs text-mut">Your sealed quote totals, visible only to you and the buyer.</span>
            </div>
            <div className="space-y-2">
              {myQuotes.map((q) => (
                <Link key={q.id} href={`/rfq/${q.rfqId}`} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                  <span className="text-sm text-navy min-w-0">
                    <span className="font-medium">{q.rfqTitle || q.rfqId}</span>
                    <span className="text-mut"> · {peso(q.total)}</span>
                  </span>
                  <span className="text-xs whitespace-nowrap ml-3">
                    {q.status === "awarded" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">Awarded</span>}
                    {q.status === "shortlisted" && <span className="chip bg-brand-green/10 text-brand-greendark">Shortlisted</span>}
                    {q.status === "submitted" && <span className="chip bg-navy-50 text-navy-600">Submitted</span>}
                    {q.status === "rejected" && <span className="chip bg-line text-mut">Not selected</span>}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* My Items — Increment 11 */}
        {myItems && myItems.length > 0 && (
          <div className="card p-5 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-navy" />
              <h3 className="font-bold text-navy">My Items for Sale / Rent</h3>
              <Link href="/post/item" className="text-xs text-brand-red font-semibold ml-auto">+ List another</Link>
            </div>
            <div className="space-y-2">
              {myItems.map((it) => (
                <div key={it.id} className="flex items-center justify-between border border-line rounded-lg p-3 gap-3">
                  <Link href={`/item/${it.id}`} className="text-sm text-navy min-w-0 hover:text-brand-red">
                    <span className="font-medium">{it.title}</span>
                    <span className="text-mut"> · {peso(it.price)}{it.mode === "rent" ? `/${it.unit || "day"}` : ""} · {it.stock} in stock</span>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    {it.status === "archived"
                      ? <span className="chip bg-line text-mut">Archived</span>
                      : <span className="chip bg-brand-green/10 text-brand-greendark">Active</span>}
                    {it.status === "active" && (
                      <button onClick={() => archiveItem(it.id)} disabled={orderBusy === it.id + "archive"} className="text-xs text-brand-red disabled:opacity-50">Remove</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Orders — Increment 11 */}
        {orders && (orders.purchases.length > 0 || orders.sales.length > 0) && (
          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            {orders.purchases.length > 0 && (
              <div className="card p-5">
                <div className="flex items-center gap-2 mb-3"><ShoppingBag className="w-4 h-4 text-navy" /><h3 className="font-bold text-navy">My Purchases</h3></div>
                <div className="space-y-2">
                  {orders.purchases.map((o) => (
                    <div key={o.id} className="border border-line rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <Link href={`/item/${o.itemId}`} className="text-sm font-medium text-navy hover:text-brand-red truncate">{o.itemTitle}</Link>
                        {statusChip(o.status)}
                      </div>
                      <div className="text-xs text-mut mt-1">{peso(o.total)} · {o.qty}× {o.mode === "rent" ? `${o.rentDays} day(s) rent` : "purchase"} · from {o.sellerName}</div>
                      {(o.status === "paid" || o.status === "fulfilled") && (
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => orderAction(o.id, "confirm")} disabled={orderBusy === o.id + "confirm"} className="btn-red h-8 px-3 text-xs disabled:opacity-50">Confirm receipt</button>
                          <button onClick={() => orderAction(o.id, "cancel")} disabled={orderBusy === o.id + "cancel"} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">Cancel</button>
                          <button onClick={() => setDisputeDeal({ dealType: "order", dealId: o.id, contextLabel: o.itemTitle })} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50">Dispute</button>
                        </div>
                      )}
                      {o.status === "disputed" && <div className="mt-2 text-xs text-brand-red">Under dispute — an admin will resolve it.</div>}
                      {o.status === "completed" && pendingByDeal.has(o.id) && (
                        <button onClick={() => setReviewDeal(pendingByDeal.get(o.id))} className="btn-outline h-8 px-3 text-xs mt-2 inline-flex"><Star className="w-3.5 h-3.5" />Leave a review</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {orders.sales.length > 0 && (
              <div className="card p-5">
                <div className="flex items-center gap-2 mb-3"><Package className="w-4 h-4 text-navy" /><h3 className="font-bold text-navy">My Sales</h3></div>
                <div className="space-y-2">
                  {orders.sales.map((o) => (
                    <div key={o.id} className="border border-line rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <Link href={`/item/${o.itemId}`} className="text-sm font-medium text-navy hover:text-brand-red truncate">{o.itemTitle}</Link>
                        {statusChip(o.status)}
                      </div>
                      <div className="text-xs text-mut mt-1">{peso(o.total)} · {o.qty}× {o.mode === "rent" ? `${o.rentDays} day(s) rent` : "sale"} · to {o.buyerName}</div>
                      <div className="flex gap-2 mt-2">
                        {o.status === "paid" && <button onClick={() => orderAction(o.id, "fulfill")} disabled={orderBusy === o.id + "fulfill"} className="btn-navy h-8 px-3 text-xs disabled:opacity-50">Mark fulfilled</button>}
                        {(o.status === "paid" || o.status === "fulfilled") && <button onClick={() => orderAction(o.id, "cancel")} disabled={orderBusy === o.id + "cancel"} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">Cancel</button>}
                        {(o.status === "paid" || o.status === "fulfilled") && <button onClick={() => setDisputeDeal({ dealType: "order", dealId: o.id, contextLabel: o.itemTitle })} className="btn-outline h-8 px-3 text-xs text-brand-red">Dispute</button>}
                        {o.status === "disputed" && <span className="text-xs text-brand-red">Under dispute</span>}
                        {o.status === "completed" && <span className="text-xs text-brand-green">Paid out to your wallet</span>}
                        {o.status === "completed" && pendingByDeal.has(o.id) && <button onClick={() => setReviewDeal(pendingByDeal.get(o.id))} className="btn-outline h-8 px-3 text-xs inline-flex"><Star className="w-3.5 h-3.5" />Review buyer</button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ratings & Reviews — Increment 12 */}
        {(pendingRevs.length > 0 || (myRevs && myRevs.count > 0)) && (
          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-3"><Star className="w-4 h-4 text-brand-gold" /><h3 className="font-bold text-navy">Your Rating</h3></div>
              {myRevs && myRevs.count > 0
                ? <><div className="mb-4"><RatingSummary average={myRevs.average} count={myRevs.count} breakdown={myRevs.breakdown} /></div>
                    <ReviewList reviews={myRevs.received} emptyText="No reviews about you yet." /></>
                : <p className="text-sm text-mut">Complete a deal and earn your first review to build your reputation.</p>}
            </div>
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-3"><Star className="w-4 h-4 text-navy" /><h3 className="font-bold text-navy">Reviews to Leave</h3></div>
              {pendingRevs.length === 0
                ? <p className="text-sm text-mut">You're all caught up — no completed deals awaiting your review.</p>
                : <div className="space-y-2">
                    {pendingRevs.map((p) => (
                      <div key={p.dealId} className="flex items-center justify-between border border-line rounded-lg p-3 gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-navy truncate">{p.contextLabel || (p.dealType === "contract" ? "Contract" : "Order")}</div>
                          <div className="text-xs text-mut">with {p.counterpartyName || "counterparty"} · {p.dealType}</div>
                        </div>
                        <button onClick={() => setReviewDeal(p)} className="btn-red h-8 px-3 text-xs shrink-0"><Star className="w-3.5 h-3.5" />Review</button>
                      </div>
                    ))}
                  </div>}
            </div>
          </div>
        )}

        {/* My Disputes — Increment 16 */}
        {disputes.length > 0 && (
          <div className="card p-5 mt-4">
            <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-brand-red" /><h3 className="font-bold text-navy">Disputes</h3></div>
            <div className="space-y-2">
              {disputes.map((d) => (
                <div key={d.id} className="border border-line rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-navy truncate">{d.contextLabel || d.dealType} · {REASON_LABELS[d.reason] || d.reason}</span>
                    {disputeStatusChip(d.status)}
                  </div>
                  <div className="text-xs text-mut mt-1">{d.iOpened ? "You opened this" : "Opened against you"} · {peso(d.amount)}{d.resolution ? ` · resolved: ${d.resolution}` : ""}</div>
                  {d.message && <p className="text-xs text-navy-700 mt-1">“{d.message}”</p>}
                  {d.response && <p className="text-xs text-mut mt-1">Response: “{d.response}”</p>}
                  {d.canRespond && <button onClick={() => setRespondTo(d)} className="btn-navy h-8 px-3 text-xs mt-2">Respond</button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {reviewDeal && <ReviewForm deal={reviewDeal} onClose={() => setReviewDeal(null)} onDone={reviewDone} />}
      {disputeDeal && <DisputeModal deal={disputeDeal} onClose={() => setDisputeDeal(null)} onDone={() => { setDisputeDeal(null); refreshDisputes(); }} />}
      {respondTo && <RespondModal dispute={respondTo} onClose={() => setRespondTo(null)} onDone={() => { setRespondTo(null); refreshDisputes(); }} />}

      {coForm && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 pb-24 sm:pb-4 bg-navy/50 backdrop-blur-sm overflow-y-auto" onClick={() => setCoForm(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitCompanyVerification} className="card w-full max-w-md p-5 sm:p-6 mt-8 sm:mt-0 mb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-extrabold text-navy">Verify your company</h3>
              <button type="button" onClick={() => setCoForm(null)} className="btn-ghost h-8 w-8 p-0">✕</button>
            </div>
            <p className="text-xs text-mut mb-4">Provide your registered business details. Our team reviews each submission before approving the verified badge.</p>
            <div className="space-y-3">
              <div><label className="label">Legal business name *</label><input className="field" value={coForm.bizName} onChange={(e) => setCoForm({ ...coForm, bizName: e.target.value })} placeholder="e.g. ABC Construction Corp." /></div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <div><label className="label">Registry</label>
                  <select className="field" value={coForm.regType} onChange={(e) => setCoForm({ ...coForm, regType: e.target.value })}>
                    <option value="DTI">DTI</option><option value="SEC">SEC</option><option value="CDA">CDA</option>
                  </select>
                </div>
                <div><label className="label">Registration no. *</label><input className="field" value={coForm.regNo} onChange={(e) => setCoForm({ ...coForm, regNo: e.target.value })} placeholder="e.g. 1234567" /></div>
              </div>
              <div><label className="label">Business address *</label><input className="field" value={coForm.address} onChange={(e) => setCoForm({ ...coForm, address: e.target.value })} placeholder="Street, City, Province" /></div>
              <label className="flex items-start gap-2 text-xs text-mut"><input type="checkbox" className="mt-0.5" checked={coForm.certify} onChange={(e) => setCoForm({ ...coForm, certify: e.target.checked })} />I certify this information is accurate and I'm authorized to represent this business. A reviewer may request supporting documents (business permit / registration).</label>
              {coErr && <p className="text-sm text-brand-red">{coErr}</p>}
              <button type="submit" disabled={verifBusy} className="btn-navy w-full disabled:opacity-60">{verifBusy ? "Submitting…" : "Submit for review"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* Increment 30: worker's job applications */
function MyApplications() {
  const [apps, setApps] = useState<any[] | null>(null);
  useEffect(() => { api.myApplications().then(setApps).catch(() => setApps([])); }, []);
  const badge: Record<string, string> = { applied: "bg-navy-50 text-navy", shortlisted: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-brand-red", withdrawn: "bg-navy-50 text-mut" };
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-4">
        <Send className="w-5 h-5 text-brand-red" />
        <h1 className="text-2xl font-extrabold text-navy">My Applications</h1>
      </div>
      {!apps ? <div className="text-mut text-sm">Loading…</div> : apps.length === 0 ? (
        <div className="card p-8 text-center text-mut">
          You haven't applied to any jobs yet. <Link href="/marketplace?tab=Jobs" className="text-brand-red font-semibold">Browse jobs</Link>
        </div>
      ) : (
        <div className="space-y-2">
          {apps.map((a) => (
            <Link key={a.id} href={`/j/${a.jobId}`} className="card p-4 flex items-center justify-between gap-3 hover:border-navy-300">
              <div className="min-w-0">
                <div className="font-semibold text-navy truncate">{a.jobTitle || a.jobId}</div>
                <div className="text-xs text-mut">{a.jobCity || ""}{a.jobStatus === "closed" ? " · job closed" : ""}</div>
              </div>
              <span className={`chip capitalize shrink-0 ${badge[a.status] || "bg-navy-50"}`}>{a.status}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* Increment 20: focused "My X" views driven by ?view= */
const VIEW_CONFIG: Record<string, { title: string; Icon: any; post?: string; postLabel?: string; empty: string; path: (x: any) => string; name: (x: any) => string }> = {
  people: { title: "My Résumés", Icon: FileText, post: "/post/resume", postLabel: "Post a résumé", empty: "You haven't posted a résumé yet.", path: (x) => `/p/${x.id}`, name: (x) => x.title || x.name },
  services: { title: "My Services", Icon: Wrench, post: "/post/service", postLabel: "Offer a service", empty: "You haven't listed any services yet.", path: (x) => `/s/${x.id}`, name: (x) => x.title },
  jobs: { title: "My Jobs", Icon: Briefcase, post: "/post/job", postLabel: "Post a job", empty: "You haven't posted any jobs yet.", path: (x) => `/j/${x.id}`, name: (x) => x.title },
  projects: { title: "My Projects", Icon: FolderKanban, post: "/post/project", postLabel: "Post a project", empty: "You haven't posted any projects yet.", path: (x) => `/project/${x.id}`, name: (x) => x.title },
  companies: { title: "My Companies", Icon: Building2, post: "/post/company", postLabel: "New company", empty: "You don't own or belong to any companies yet.", path: (x) => `/c/${x.id}`, name: (x) => x.name },
  bids: { title: "My Bids", Icon: Gavel, empty: "You haven't placed any bids yet.", path: (x) => `/project/${x.projectId}`, name: (x) => x.projectTitle || x.projectId },
};

function FocusedView({ view, mine, myBids, companies }: { view: string; mine: Mine | null; myBids: any[] | null; companies: any[] }) {
  const cfg = VIEW_CONFIG[view];
  if (!cfg) return <div className="card p-8 text-center text-mut">Unknown section.</div>;
  const rows: any[] = view === "bids" ? (myBids ?? []) : view === "companies" ? companies : ((mine as any)?.[view] ?? []);
  const { Icon } = cfg;
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Link href="/dashboard" className="btn-ghost h-9 w-9 p-0" title="Back to dashboard">←</Link>
          <Icon className="w-5 h-5 text-navy" />
          <h1 className="text-2xl font-extrabold text-navy">{cfg.title}</h1>
          <span className="chip bg-navy-50 text-navy-600">{rows.length}</span>
        </div>
        {cfg.post && <Link href={cfg.post} className="btn-navy"><Plus className="w-4 h-4" />{cfg.postLabel}</Link>}
      </div>
      {rows.length === 0 ? (
        <div className="card p-10 text-center">
          <Icon className="w-8 h-8 text-mut mx-auto mb-2" />
          <div className="font-bold text-navy">{cfg.empty}</div>
          {cfg.post && <Link href={cfg.post} className="btn-red mt-4 inline-flex"><Plus className="w-4 h-4" />{cfg.postLabel}</Link>}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((x, i) => (
            <div key={x.id || i} className="card p-3 flex items-center justify-between gap-3">
              <Link href={cfg.path(x)} className="text-sm font-semibold text-navy hover:text-brand-red truncate min-w-0">{cfg.name(x)}</Link>
              <div className="flex items-center gap-2 shrink-0">
                {view === "companies" && x.role && <span className="chip bg-navy-50 text-navy-600 capitalize">{x.role}</span>}
                {view === "companies" && (x.role === "owner" || x.role === "admin") && <Link href={`/company/${x.id}/manage`} className="btn-outline h-8 px-3 text-xs">Manage</Link>}
                {view === "bids" && <span className="chip bg-navy-50 text-navy-600 capitalize">{x.status}</span>}
                {view !== "companies" && view !== "bids" && <span className="text-xs text-brand-green">● Live</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
